import { useAuthStore } from '../store/auth';

/**
 * 仪表盘视图角色：admin 管理员 / duty 值班长 / leader 组长 / agent 坐席。
 *
 * ⚠️ 必须由「角色绑定集合」解析，不能读 `sys_user.role`：组内角色是主管档的细分，
 * 主角色都归一成 `supervisor`（见 server internal/model/user_role.go）。
 *
 * 本仓部署口径：**值班长 = `supervisor.deputy`**（后端语义里该码叫「副组长」，与班组长同档同权，
 * 仅组内角色标识不同）。组长 = `supervisor.leader` 或未绑组内角色的普通 `supervisor`。
 * 若以后改用独立角色码，只改本文件一处即可，模板与卡片不用动。
 *
 * 兜底：非主管档账号即使绑了组内角色也只得坐席视图——`/monitor/screen/overview` 等全场接口
 * 只认 admin/supervisor，硬闯只会 403。
 */
export type ViewRole = 'admin' | 'duty' | 'leader' | 'agent';

/** 值班长的本仓登记码（后端语义为「副组长」）；改口径优先改这里 */
export const DUTY_ROLE_CODE = 'supervisor.deputy';

/**
 * 值班长角色码判定：兼容部署期出现的几种写法（`supervisor.deputy` / `supervisor.duty` /
 * 自建的 `duty` / `duty_manager` / 中文码含「值班长」）。按分隔符切段比对，避免 `study` 之类误命中。
 */
export function isDutyRoleCode(code: string): boolean {
  if (!code) return false;
  if (code === DUTY_ROLE_CODE) return true;
  if (code.includes('值班长')) return true;
  const segs = code.toLowerCase().split(/[._-]/);
  return segs.includes('duty') || segs.includes('deputy');
}

/** 主管档：admin 之外的 supervisor 及其细分码（含数据库里可能新增的 supervisor.* 细分） */
function isSupervisorTier(role: string | undefined, codes: string[]): boolean {
  if (role === 'admin') return true;
  if (role && role.startsWith('supervisor')) return true;
  return codes.some((c) => c === 'admin' || c === 'supervisor' || c.startsWith('supervisor.'));
}

/**
 * 角色码是否属于「主管档」：某些部署把值班长做成独立码（如 `duty`），此时凭角色码本身
 * 也应当按主管档处理，否则该账号会被判成坐席。
 */
function isSupervisorRoleCode(code: string): boolean {
  if (!code) return false;
  if (code === 'supervisor' || code === 'admin') return true;
  if (code.startsWith('supervisor.')) return true;
  const segs = code.toLowerCase().split(/[._-]/);
  return segs.includes('supervisor') || segs.includes('leader') || isDutyRoleCode(code);
}

/** 纯函数便于单测与复用：判定顺序 admin > duty > leader > agent（兼任取高） */
export function resolveViewRole(role: string | undefined, roleCodes: string[]): ViewRole {
  const codes = roleCodes || [];
  if (role === 'admin' || codes.includes('admin')) return 'admin';
  const dutyCode = codes.find((c) => isDutyRoleCode(c));
  const tier = isSupervisorTier(role, codes) || codes.some((c) => isSupervisorRoleCode(c));
  if (!tier) return 'agent';
  // 主管档内再分：值班长看全场、组长看本组；两者都绑时取值班长（兼任取高）
  return dutyCode ? 'duty' : 'leader';
}

export function useViewRole(): ViewRole {
  const role = useAuthStore((s) => s.user?.role);
  const roles = useAuthStore((s) => s.rbac?.roles);
  return resolveViewRole(role, roles ?? []);
}
